import { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

// API hook for the digital table plan ("Tischplan"): the room layout of one
// entertainment — where its tables stand, plus walls, doors and free text
// labels. Which table numbers exist is not part of the plan; the backend
// derives them from the entertainment's maxTableNumber and returns them with
// the plan, so the editor knows what is still unplaced.
const useTablePlan = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, sendRequest } = useHttp();

  const request = useCallback(
    async ({ url, method = 'GET', body, toastMessage }) => {
      try {
        return await sendRequest({
          url,
          method,
          body,
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        if (toastMessage) toast.error(toastMessage);
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const getPlannableEntertainments = useCallback(
    async () =>
      request({
        url: '/api/v1/tablePlan/entertainments',
        toastMessage: 'Fehler beim Laden der Standorte',
      }),
    [request]
  );

  const getTablePlan = useCallback(
    async entertainmentId =>
      request({
        url: `/api/v1/tablePlan/${entertainmentId}`,
        toastMessage: 'Fehler beim Laden des Tischplans',
      }),
    [request]
  );

  const saveTablePlan = useCallback(
    async (entertainmentId, payload) =>
      request({
        url: `/api/v1/tablePlan/${entertainmentId}`,
        method: 'PUT',
        body: payload,
        toastMessage: 'Fehler beim Speichern des Tischplans',
      }),
    [request]
  );

  const resetTablePlan = useCallback(
    async entertainmentId =>
      request({
        url: `/api/v1/tablePlan/${entertainmentId}`,
        method: 'DELETE',
        toastMessage: 'Fehler beim Zurücksetzen des Tischplans',
      }),
    [request]
  );

  const getOccupancy = useCallback(
    async entertainmentId =>
      request({
        url: `/api/v1/tablePlan/${entertainmentId}/occupancy`,
        toastMessage: 'Fehler beim Laden der Tischbelegung',
      }),
    [request]
  );

  return {
    isLoading,
    error,
    getPlannableEntertainments,
    getTablePlan,
    saveTablePlan,
    resetTablePlan,
    getOccupancy,
  };
};

export default useTablePlan;
